import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Container,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase-config';
import AuthDiagnostics from './AuthDiagnostics';
import '../utils/authDiagnostics'; // Load diagnostics utility

const LoginScreen = () => {
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'code'
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // Default to remember
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const { signInWithPhone, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated (use context, not direct supabase call)
  React.useEffect(() => {
    if (!authLoading && user) {
      const destination = (user.role === 'admin' || user.role === 'manager') 
        ? '/manager' 
        : '/tech';
      console.log('🚀 Already authenticated, redirecting to:', destination);
      navigate(destination, { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Countdown timer for OTP resend
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Format phone display
  const formatPhoneDisplay = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits;
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  // Phone OTP - Send Code
  const handleSendCode = async (e) => {
    e.preventDefault();
    
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid phone number.');
      return;
    }

    if (!consentChecked) {
      setError('You must agree to receive SMS messages to continue.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    // Log consent before sending OTP
    try {
      const response = await fetch('/api/consent-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          consentChecked: true,
          consentTextVersion: process.env.REACT_APP_CONSENT_TEXT_VERSION || 'v1-2025-09-09'
        })
      });
      
      if (!response.ok) {
        console.warn('Consent logging failed, but continuing with OTP');
      }
    } catch (err) {
      console.warn('Consent logging error:', err);
    }

    // Store remember me preference
    if (rememberMe) {
      localStorage.setItem('worcester-scale-remember', 'true');
    } else {
      localStorage.removeItem('worcester-scale-remember');
    }
    
    const result = await signInWithPhone(phone, 'send');
    
    if (result.success) {
      setStep('code');
      setMessage(`Code sent to ${formatPhoneDisplay(phone)}`);
      setCountdown(60);
    } else {
      setError(result.error || 'Failed to send code. Please check your phone number.');
    }
    
    setLoading(false);
  };

  // Phone OTP - Verify Code
  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter the 6-digit verification code.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('Verifying code...');

    try {
      const result = await signInWithPhone(phone, 'verify', otpCode);

      if (!result.success) {
        setError(result.error || 'Invalid verification code.');
        setLoading(false);
      } else {
        // Success - verification initiated
        setMessage('Authentication successful! Establishing session...');

        // The auth state listener in App.js will handle navigation
        // We just need to wait for it to trigger
        console.log('✅ OTP verified successfully, waiting for auth state handler...');

        // Set a timeout as a fallback in case auth state doesn't trigger
        setTimeout(async () => {
          // Check if we're still on the login page after 3 seconds
          if (window.location.pathname === '/login') {
            console.log('⚠️ Auth state handler timeout, checking session manually...');

            const { data: { session } } = await supabase.auth.getSession();

            if (session?.user) {
              console.log('✅ Session found, navigating manually...');

              // Get user profile to determine destination
              const { data: profile } = await supabase
                .from('users')
                .select('*')
                .eq('id', session.user.id)
                .single();

              if (profile) {
                const destination = (profile.role === 'admin' || profile.role === 'manager')
                  ? '/manager'
                  : '/tech';

                console.log('🚀 Manual navigation to:', destination);
                navigate(destination);
              } else {
                // No profile found, try with phone number
                const { data: profileByPhone } = await supabase
                  .from('users')
                  .select('*')
                  .eq('phone', session.user.phone)
                  .single();

                if (profileByPhone) {
                  const destination = (profileByPhone.role === 'admin' || profileByPhone.role === 'manager')
                    ? '/manager'
                    : '/tech';

                  navigate(destination);
                } else {
                  // Default to tech dashboard
                  navigate('/tech');
                }
              }
            }
          }
        }, 3000);
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Verification failed. Please try again.');
      setLoading(false);
    }
  };

  const resetPhoneFlow = () => {
    setStep('phone');
    setOtpCode('');
    setError('');
    setMessage('');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            p: 4,
            width: '100%',
            maxWidth: 400,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            Worcester Scale Co., Inc.
          </Typography>
          
          <Typography variant="h6" component="h2" gutterBottom color="textSecondary">
            Professional Scale Inspection System
          </Typography>
          
          {step === 'phone' ? (
            <Box component="form" onSubmit={handleSendCode} sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                value={formatPhoneDisplay(phone)}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                margin="normal"
                required
                disabled={loading}
                autoComplete="tel"
                autoFocus
                placeholder="(555) 123-4567"
                helperText="Enter your registered phone number"
                inputProps={{ maxLength: 14 }}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={consentChecked}
                    onChange={(e) => setConsentChecked(e.target.checked)}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    By logging in I consent to receive messages from Worcester Scale Co. 
                    Reply STOP to opt out or HELP for help at any time.
                  </Typography>
                }
                sx={{ mt: 2, alignItems: 'flex-start' }}
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Keep me logged in for 7 days
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading || phone.replace(/\D/g, '').length < 10 || !consentChecked}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? 'Sending Code...' : 'Send Code'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerifyCode} sx={{ mt: 3 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                We sent a 6-digit code to {formatPhoneDisplay(phone)}
              </Typography>
              
              <TextField
                fullWidth
                label="Verification Code"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                margin="normal"
                required
                disabled={loading}
                autoComplete="one-time-code"
                autoFocus
                placeholder="123456"
                inputProps={{ 
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' }
                }}
              />
              
              {message && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {message}
                </Alert>
              )}
              
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading || otpCode.length !== 6}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </Button>
              
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  onClick={resetPhoneFlow}
                  disabled={countdown > 0}
                  variant="text"
                  size="small"
                >
                  {countdown > 0 
                    ? `Resend code in ${countdown}s`
                    : 'Use different phone number'
                  }
                </Button>
              </Box>
            </Box>
          )}
          
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary" textAlign="center">
              Need help? Contact your manager to verify your phone number is registered.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }} textAlign="center">
              <Link href="/consent.html" target="_blank" rel="noopener" color="primary">
                View SMS Consent Policy
              </Link>
            </Typography>
            
            {/* Diagnostic Tools */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <IconButton
                size="small"
                onClick={() => setDiagnosticsOpen(true)}
                title="Run Authentication Diagnostics"
                color="primary"
              >
                <BugReportIcon />
              </IconButton>
              <Typography variant="caption" display="block" color="textSecondary">
                Authentication Issues? Run Diagnostics
              </Typography>
            </Box>
          </Box>
          
          {/* Diagnostics Dialog */}
          <AuthDiagnostics 
            open={diagnosticsOpen} 
            onClose={() => setDiagnosticsOpen(false)} 
          />
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginScreen;